import { useMemo, useState } from 'react';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { SectionEditDrawer } from '@/components/common/SectionEditDrawer';
import { AvatarUploadField } from '@/components/common/AvatarUploadField';
import { TextField } from '@/components/forms/TextField';
import { ComboboxField } from '@/components/forms/ComboboxField';
import { PhoneField } from '@/components/forms/PhoneField';
import { useCountries } from '@/hooks/useCountries';
import { useUpdateProfile } from '@/hooks/useProfile';
import { useUploadsInFlight } from '@/hooks/useUploadsInFlight';
import { toast } from '@/lib/toast';
import { getApiErrorMessage } from '@/lib/utils/error.utils';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import { PROFILE_LAYOUT_STRINGS as L } from '@/lib/constants/profile-layout-strings';
import type { IndividualProfile } from '@/lib/types/profile.types';
import {
  accountEditSchema,
  toApiPayload,
  toFormValues,
  type AccountEditFormValues,
} from './schema';

const P = UI_STRINGS.PROFILE;
const FIELDS = ['fullName', 'phone', 'countryId', 'avatarFileKey'] as const;

interface PersonalDetailsDrawerProps {
  open: boolean;
  onClose: () => void;
  profile: IndividualProfile;
  /** The avatar's initials fallback. */
  initials: string;
}

/** Country picker: the shared countries table, filtered in memory (250 rows, fetched in full). */
function CountryField({ form }: { form: UseFormReturn<AccountEditFormValues> }) {
  const [query, setQuery] = useState('');
  const { data: countries = [] } = useCountries();
  const selected = countries.find((country) => country.id === form.watch('countryId'));
  const options = countries
    .filter((country) => country.name.toLowerCase().includes(query.toLowerCase()))
    .map((country) => ({ value: String(country.id), label: country.name }));
  return (
    <ComboboxField
      label={P.FIELD_COUNTRY}
      labelVariant="caps"
      inputValue={query}
      onInputChange={setQuery}
      selectedLabel={selected?.name}
      options={options}
      error={form.formState.errors.countryId?.message}
      onSelect={(option) => {
        form.setValue('countryId', Number(option.value), { shouldDirty: true });
        setQuery('');
      }}
    />
  );
}

/** Phone with a country picker that starts on the person's own country when one is chosen. */
function ProfilePhoneField({ form }: { form: UseFormReturn<AccountEditFormValues> }) {
  const { data: countries = [] } = useCountries();
  const countryId = form.watch('countryId');
  return (
    <Controller
      control={form.control}
      name="phone"
      render={({ field }) => (
        <PhoneField
          {...field}
          label={P.FIELD_PHONE}
          defaultCountryIso2={countries.find((country) => country.id === countryId)?.iso2}
          legacyText={form.watch('legacyPhone')}
          onClearLegacy={() => form.setValue('legacyPhone', '', { shouldDirty: true })}
          error={form.formState.errors.phone?.message}
        />
      )}
    />
  );
}

function PersonalDetailsFields({
  form,
  profile,
  initials,
  trackUpload,
}: {
  form: UseFormReturn<AccountEditFormValues>;
  profile: IndividualProfile;
  initials: string;
  trackUpload: (id: string) => (isUploading: boolean) => void;
}) {
  return (
    <div className="flex flex-col gap-6">
      {/* Preview stays on the resolved `avatarUrl`; a bare S3 key is not a loadable image. */}
      <AvatarUploadField
        onUploadingChange={trackUpload('avatar')}
        photoUrl={profile.avatarUrl ?? undefined}
        initials={initials}
        uploadKind="individualAvatar"
        accept="image/png,image/jpeg"
        uploadCaption={P.UPLOAD_PHOTO}
        tooLargeMessage={P.PHOTO_TOO_LARGE}
        errorMessage={P.PHOTO_UPLOAD_ERROR}
        onUploaded={(result) =>
          form.setValue('avatarFileKey', result.fileKey, { shouldDirty: true })
        }
      />
      <TextField
        {...form.register('fullName')}
        label={P.FIELD_FULL_NAME}
        labelVariant="caps"
        error={form.formState.errors.fullName?.message}
      />
      <div className="grid gap-6 sm:grid-cols-2">
        <ProfilePhoneField form={form} />
        <CountryField form={form} />
      </div>
    </div>
  );
}

/**
 * Personal details side panel (FR-179): photo, Full Name on its own row, then
 * Phone and Country side by side. Replaces the old `/me/edit` page (MYN-217).
 * The email has its own card, not a field here.
 */
export function PersonalDetailsDrawer({
  open,
  onClose,
  profile,
  initials,
}: PersonalDetailsDrawerProps) {
  const uploads = useUploadsInFlight();
  const update = useUpdateProfile({
    onSaved: () => {
      onClose();
      toast.success(L.SAVE_SUCCESS);
    },
  });
  const defaultValues = useMemo(() => toFormValues(profile), [profile]);

  return (
    <SectionEditDrawer<AccountEditFormValues>
      open={open}
      onClose={onClose}
      title={P.SECTION_PERSONAL}
      schema={accountEditSchema}
      fields={FIELDS}
      defaultValues={defaultValues}
      onSubmit={(values) => update.mutate(toApiPayload(values, profile.phone))}
      isSaving={update.isPending}
      errorMessage={update.isError ? getApiErrorMessage(update.error, P.SAVE_ERROR) : undefined}
      saveDisabled={uploads.isUploading}
    >
      {(form) => (
        <PersonalDetailsFields
          form={form}
          profile={profile}
          initials={initials}
          trackUpload={uploads.track}
        />
      )}
    </SectionEditDrawer>
  );
}
